import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
    BlockWrapper,
    ChildrenBlock,
    defineBlock,
    smartProps,
    useInspectorControl,
} from '@smartfire/js-block-engine'
import style from './block.module.scss'
import Top from './Top'
import Main from './Main'
import MainMobile from './mobile/Main'
import StickyHeader from './components/Sticky'
import MenuMobile from './mobile/Menu'
import { menuItemParams } from '../types'
import clsx from 'clsx'
import { useMediaQuery } from '../hooks'

const InspectorLazy = React.lazy(() => import('./Inspector'))

export type socialsParams = {
    facebook: string
    instagram: string
    twitter: string
    youtube: string
    linkedin: string
    tiktok: string
}

type headerParams = {
    menus: { instances: menuItemParams[]; top: menuItemParams[]; main: menuItemParams[] }
    ticketting: string
    myHand: string
    handballTV: string
    socials: socialsParams
    title: string
    findClub: string
    handHome: string
    noBackground: boolean
}

const Header: React.FC<headerParams> = ({
    menus,
    ticketting,
    myHand,
    handballTV,
    socials,
    findClub,
    handHome,
    noBackground,
}) => {
    useInspectorControl(InspectorLazy, [])

    const [sticky, setSticky] = useState<boolean>(false)
    const [active, setActive] = useState<boolean>(false)
    const [activeMenu, setActiveMenu] = useState<number | null>(null)
    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const headerRef = useRef<HTMLDivElement>(null)

    const tablet = useMediaQuery('(max-width: ' + style.tabletBreakpoint + ')')

    const handleActive = () => {
        setActive((old) => {
            if (old) {
                document.body.style.overflow = 'visible'
            } else {
                document.body.style.overflow = 'hidden'
            }
            return !old
        })
    }

    const toggleSearch = useCallback(() => {
        setIsSearchOpen((p) => !p)
    }, [])

    // Sticky menu on scroll
    useEffect(() => {
        if (!headerRef.current) return

        const handleScroll = () => {
            if (headerRef.current) {
                let topElementSize = headerRef.current?.getBoundingClientRect().height

                const bannerContainerElement = document.getElementById('smartfire-banner-container')
                if (bannerContainerElement) {
                    topElementSize += bannerContainerElement.getBoundingClientRect().height
                }

                setSticky(window.scrollY > topElementSize)
            }
        }

        document.addEventListener('scroll', handleScroll)

        return () => {
            document.removeEventListener('scroll', handleScroll)
        }
    }, [headerRef])

    return (
        <BlockWrapper.Header
            className={clsx(style.header, noBackground ? style.noBackground : undefined)}
        >
            <div ref={headerRef}>
                <Top
                    menus={{ instances: menus?.instances, top: menus?.top }}
                    externals={{ ticketting, myHand, handballTV, handHome }}
                    socials={socials}
                />
                <div className={style.main}>
                    {tablet ? (
                        <MainMobile
                            setActive={handleActive}
                            isSearchOpen={isSearchOpen}
                            toggleSearch={toggleSearch}
                        />
                    ) : (
                        <Main
                            menu={menus.main}
                            external={findClub}
                            sticky={sticky}
                            active={activeMenu}
                            setActive={setActiveMenu}
                            isSearchOpen={isSearchOpen}
                            toggleSearch={toggleSearch}
                        />
                    )}

                    <ChildrenBlock
                        gutenbergEditorConfig={{
                            allowedBlocks: ['smartfire/page-header'],
                            template: [['smartfire/page-header']],
                            templateLock: true,
                        }}
                        className={activeMenu ? style.none : undefined}
                    />
                </div>
            </div>
            <StickyHeader sticky={sticky}>
                {tablet ? (
                    <MainMobile
                        setActive={handleActive}
                        isSearchOpen={isSearchOpen}
                        toggleSearch={toggleSearch}
                    />
                ) : (
                    <Main
                        menu={menus.main}
                        external={findClub}
                        active={activeMenu}
                        setActive={setActiveMenu}
                        isSearchOpen={isSearchOpen}
                        toggleSearch={toggleSearch}
                    />
                )}
            </StickyHeader>
            <MenuMobile
                menus={{ top: menus?.top, main: menus?.main }}
                active={active}
                setActive={handleActive}
                externals={{ findClub, ticketting, myHand, handballTV, handHome }}
                socials={socials}
            />
        </BlockWrapper.Header>
    )
}

export default defineBlock({
    Block: Header,
    smartProps: {
        ticketting: smartProps.string.withDefault(''),
        myHand: smartProps.string.withDefault(''),
        handballTV: smartProps.string.withDefault(''),
        findClub: smartProps.string.withDefault(''),
        handHome: smartProps.string.withDefault(''),
        menus: smartProps.object
            .withDefault({
                instances: [],
                top: [],
                main: [],
            })
            .contentServerSide(),
        socials: smartProps.object.withDefault({}),
    },
    setup: {
        title: 'Header',
    },
})
